import { useEffect, useMemo } from "react";
import { CheckBox } from "@etframework/ui.components";
import { useTranslation } from "react-i18next";
import Enums from "../../types/enums";

export const PermissionsTable = ({ rolePermissionsData, isEditing, id }) => {
  const { t } = useTranslation(["RolePermissions"]);
  var rolePermissions = rolePermissionsData?.RolePermissions;

  const handleCanModifyCheckboxChange = (args) => {
    rolePermissionsData.CanModifyTasks = args.checked;
  };

  const handleCanCompleteCheckboxChange = (args, id) => {
    let permissionToChange = rolePermissions?.find((item) => item.Id === id);
    permissionToChange.CanComplete = args.checked;
  };

  const handleCanDeleteCheckboxChange = (args, id) => {
    let permissionToChange = rolePermissions?.find((item) => item.Id === id);
    permissionToChange.CanDelete = args.checked;
  };

  const permissions = useMemo(
    () => ({
      admin: rolePermissions?.filter(
        (x) => x.Role === Enums.AppRole.Administrator
      ),
      manager: rolePermissions?.filter((x) => x.Role === Enums.AppRole.Manager),
      staff: rolePermissions?.filter((x) => x.Role === Enums.AppRole.Staff),
      dataEntry: rolePermissions?.filter(
        (x) => x.Role === Enums.AppRole.DataEntry
      ),
    }),
    [rolePermissions]
  );

  useEffect(() => {
    /* There is an unkwon issue with duplicate roles */
    for (const attr in permissions) {
      if (permissions[attr]?.length > 1)
        console.error(`Error: There are duplicate ${attr} role records`);
    }
  }, [permissions]);

  const CanModifyTemplate = ({ id }) => (
    <div className="permission-content">
      {isEditing ? (
        <CheckBox
          id={id}
          checked={rolePermissionsData?.CanModifyTasks}
          change={(args) => handleCanModifyCheckboxChange(args)}
        />
      ) : (
        <i
          className={`${
            rolePermissionsData?.CanModifyTasks
              ? "fa-solid fa-check"
              : "fa-solid fa-x"
          } icon`}
        ></i>
      )}
    </div>
  );

  const CanCompleteTemplate = ({ rolePermission, id }) => {
    const isAdmin = rolePermission?.Role === Enums.AppRole.Administrator;

    return (
      <td className="permission-content">
        {isEditing ? (
          <CheckBox
            id={id}
            disabled={isAdmin}
            checked={rolePermission?.CanComplete}
            change={(args) =>
              handleCanCompleteCheckboxChange(args, rolePermission?.Id)
            }
          />
        ) : (
          <i
            className={`${
              rolePermission?.CanComplete
                ? "fa-solid fa-check"
                : "fa-solid fa-x"
            } icon`}
          ></i>
        )}
      </td>
    );
  };

  const CanDeleteTemplate = ({ rolePermission, id }) => {
    const isAdmin = rolePermission?.Role === Enums.AppRole.Administrator;

    return (
      <td className="permission-content">
        {isEditing ? (
          <CheckBox
            id={id}
            disabled={isAdmin}
            checked={rolePermission?.CanDelete}
            change={(args) =>
              handleCanDeleteCheckboxChange(args, rolePermission?.Id)
            }
          />
        ) : (
          <i
            className={`${
              rolePermission?.CanDelete ? "fa-solid fa-check" : "fa-solid fa-x"
            } icon`}
          ></i>
        )}
      </td>
    );
  };

  return (
    <div className="m-4 d-flex justify-content-between" id={id}>
      <div className="col-3">
        <span className="user-permission-title">
          {t("PermissionsTable.Title")}
        </span>
      </div>
      <div className="col-9">
        <div className="bc-bg-light-gray py-2 mb-3 can-modify">
          <div className="d-flex justify-content-start gap-3 m-3">
            <div>
              <CanModifyTemplate id={id + "_can-modify-checkbox"} />
            </div>
            <div>
              <div className="fw-600 fs-14">
                {t("PermissionsTable.CanModifyTitle")}
              </div>
              <div className="fs-12 et-darkest-gray">
                {t("PermissionsTable.CanModifySubtitle")}
              </div>
            </div>
          </div>
        </div>
        <table className="user-permissions-table bc-bg-light-gray br-4">
          <thead>
            <tr className="border-bottom bg-et-lightest-gray">
              <th></th>
              <th>{t("Admin")}</th>
              <th>{t("Managers")}</th>
              <th>{t("Staff")}</th>
              <th>{t("DataEntry")}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <div className="permission-description">
                  <span className="span-block title">
                    {t("PermissionsTable.Row1Msg1")}
                  </span>
                  <span className="span-block description">
                    {t("PermissionsTable.Row1Msg2")}
                  </span>
                </div>
              </td>
              <CanCompleteTemplate
                id={id + "_can-complete_admin-checkbox"}
                rolePermission={permissions?.admin?.[0]}
              />
              <CanCompleteTemplate
                id={id + "_can-complete_manager-checkbox"}
                rolePermission={permissions?.manager?.[0]}
              />
              <CanCompleteTemplate
                id={id + "_can-complete_staff-checkbox"}
                rolePermission={permissions?.staff?.[0]}
              />
              <CanCompleteTemplate
                id={id + "_can-complete_data-entry-checkbox"}
                rolePermission={permissions?.dataEntry?.[0]}
              />
            </tr>
            <tr className="border-top">
              <td>
                <div className="permission-description">
                  <span className="span-block title">
                    {t("PermissionsTable.Row2Msg1")}
                  </span>
                  <span className="span-block description">
                    {t("PermissionsTable.Row2Msg2")}
                  </span>
                </div>
              </td>
              <CanDeleteTemplate
                id={id + "_can-delete_admin-checkbox"}
                rolePermission={permissions?.admin?.[0]}
              />
              <CanDeleteTemplate
                id={id + "_can-delete_manager-checkbox"}
                rolePermission={permissions?.manager?.[0]}
              />
              <CanDeleteTemplate
                id={id + "_can-delete_staff-checkbox"}
                rolePermission={permissions?.staff?.[0]}
              />
              <CanDeleteTemplate
                id={id + "_can-delete_data-entry-checkbox"}
                rolePermission={permissions?.dataEntry?.[0]}
              />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};
